"use client";

import { Star } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import {
	getHomepageTickerQuote,
	type HomepageReview,
} from "@/lib/homepage-reviews";

interface ReviewsInteractiveTickerProps {
	reviews: HomepageReview[];
}

const STAR_POSITIONS = [1, 2, 3, 4, 5] as const;

export default function ReviewsInteractiveTicker({
	reviews,
}: ReviewsInteractiveTickerProps) {
	const [isInteractive, setIsInteractive] = useState(false);

	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setIsInteractive(true);
		}, 0);

		return () => window.clearTimeout(timeoutId);
	}, []);

	if (!isInteractive) return null;

	return (
		<div
			className="mt-12 md:mt-16"
			data-testid="homepage-testimonials-featured"
		>
			<h3 className="font-syne text-2xl font-black leading-none text-zinc-950 md:text-4xl">
				More of our satisfied customers
			</h3>

			<div
				className="homepage-testimonials-ticker-bleed"
				data-testid="homepage-testimonials-ticker-bleed"
			>
				<div className="homepage-testimonials-ticker-mask">
					<div className="homepage-testimonials-ticker-motion flex w-max items-stretch">
						<TickerTrack reviews={reviews} />
						<TickerTrack reviews={reviews} duplicate />
					</div>
				</div>
			</div>
		</div>
	);
}

function TickerTrack({
	reviews,
	duplicate = false,
}: ReviewsInteractiveTickerProps & { duplicate?: boolean }) {
	return (
		<ul
			aria-hidden={duplicate ? "true" : undefined}
			className="homepage-testimonials-ticker-track flex shrink-0 list-none items-stretch gap-4 px-2 md:gap-5 md:px-3"
			data-testid={
				duplicate
					? "homepage-testimonials-ticker-track-duplicate"
					: "homepage-testimonials-ticker-track"
			}
		>
			{reviews.map((review) => (
				<li
					className="w-[18rem] shrink-0 md:w-[22rem] lg:w-[24rem]"
					key={`${duplicate ? "duplicate-" : ""}${review.id}`}
				>
					<TickerCard review={review} />
				</li>
			))}
		</ul>
	);
}

function TickerCard({ review }: { review: HomepageReview }) {
	return (
		<article
			className="flex h-full min-h-[16rem] flex-col justify-between rounded-lg border border-zinc-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(24,24,27,0.07)]"
			data-testid="homepage-testimonial-ticker-card"
		>
			<div>
				<RatingStars review={review} />
				<p className="mt-5 font-inter text-base font-semibold leading-6 text-zinc-900 md:text-lg md:leading-7">
					&ldquo;{getHomepageTickerQuote(review)}&rdquo;
				</p>
			</div>

			<div className="mt-8 flex items-center gap-3">
				<div className="relative size-11 shrink-0 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100">
					<Image
						alt={`${review.reviewer} portfolio image`}
						className="h-full w-full object-cover"
						height={80}
						sizes="44px"
						src={review.avatarSrc}
						width={80}
					/>
				</div>

				<div className="min-w-0">
					<h4 className="truncate font-syne text-base font-black leading-tight text-zinc-950">
						{review.reviewer}
					</h4>
					<p className="mt-0.5 truncate font-inter text-xs font-semibold leading-5 text-zinc-600">
						{review.role}
					</p>
				</div>
			</div>
		</article>
	);
}

function RatingStars({ review }: { review: HomepageReview }) {
	const fullStarCount = Math.floor(review.rating);
	const hasHalfStar = review.rating % 1 !== 0;

	return (
		<div className="flex items-center gap-1.5 text-red-600">
			<span className="sr-only">{review.rating} out of 5 stars</span>
			{STAR_POSITIONS.map((position) => {
				if (position <= fullStarCount) {
					return (
						<Star
							aria-hidden="true"
							className="size-4 fill-current"
							key={`${review.id}-ticker-star-${position}`}
							strokeWidth={2.4}
						/>
					);
				}

				if (position === fullStarCount + 1 && hasHalfStar) {
					return (
						<span
							aria-hidden="true"
							key={`${review.id}-ticker-star-half`}
							className="relative inline-flex size-4"
						>
							<Star
								className="size-4 fill-current text-zinc-300"
								data-testid="homepage-testimonial-half-star-base"
								strokeWidth={2.4}
							/>
							<Star
								className="absolute inset-0 size-4 fill-current text-red-600 [clip-path:inset(0_50%_0_0)]"
								strokeWidth={2.4}
							/>
						</span>
					);
				}

				return null;
			})}
		</div>
	);
}
